import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { MapPin, Building2, Mail, Phone, Unlock, Coins, ArrowLeft, Users } from 'lucide-react';
import ShareButton from '../../../components/ShareButton';
import apiClient from '../../../services/apiClient';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '350px', borderRadius: '8px' };
const libraries = ['places'];

export const OrganizationProfileDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);

  useEffect(() => {
    fetchOrgProfile();
    if (token) {
      fetchWallet();
      checkPurchaseStatus();
    }
  }, [orgId, token]);

  const fetchOrgProfile = async () => {
    try {
      const data = await apiClient.get(`/organizations/${orgId}`);
      setOrg(data);
    } catch (error) {
      toast.error('Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const fetchWallet = async () => {
    try {
      const data = await apiClient.get('/wallet/balance');
      setWallet({ balance: data.balance || 0 });
    } catch (error) {
      console.error('Failed to fetch wallet');
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const data = await apiClient.get(
        `/check-purchase-status?target_type=organization_profile&target_id=${orgId}`
      );
      if (data.purchased && !data.expired) {
        setContactUnlocked(true);
        setContactDetails(data.contact_details);
      }
    } catch (error) {
      console.error('Failed to check purchase status');
    }
  };

  const handleUnlock = async () => {
    if (!token) {
      toast.error('Please login to unlock contact');
      navigate('/login');
      return;
    }

    if (wallet.balance < org.unlock_price) {
      toast.error(`Insufficient balance. Need ${org.unlock_price} coins`);
      return;
    }

    setUnlocking(true);
    try {
      const data = await apiClient.post(
        `/unlock-contact?target_type=organization_profile&target_id=${orgId}`
      );

      toast.success(`Contact unlocked! ${data.coins_deducted} coins deducted`);
      setContactUnlocked(true);
      setContactDetails(data.contact_details);
      fetchWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock contact');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div></div>;
  }

  if (!org) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p className="text-xl">Organization not found</p></div>;
  }

  const mapCenter = org.latitude && org.longitude ? { lat: org.latitude, lng: org.longitude } : { lat: 28.6139, lng: 77.2090 };
  const isApproved = org.review_status === 'approved';

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
        <Button variant="outline" onClick={() => navigate('/find-organisation')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Organizations
        </Button>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <Card>
              <CardContent className="pt-6 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  {(org.logo_url || org.image) ? (
                    <img src={org.logo_url || org.image} alt={org.organization_name || org.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border-2 border-blue-100 flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"><Building2 className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600" /></div>
                  )}
                  <div className="flex-1 text-center sm:text-left w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1">
                        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">{org.organization_name || org.name}</h1>
                        <p className="text-base sm:text-lg text-gray-600 capitalize">{(org.organization_type || org.type || '').replace(/_/g, ' ')}</p>
                      </div>
                      <ShareButton
                        title={org.organization_name || org.name}
                        text={`Check out ${org.organization_name} - ${(org.organization_type || org.type || '').replace(/_/g, ' ')}`}
                        variant="outline"
                        size="default"
                      />
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap justify-center sm:justify-start">
                      {isApproved && <Badge className="bg-green-500">Verified</Badge>}
                      {(org.total_students || org.students) > 0 && <Badge variant="outline"><Users className="h-3 w-3 mr-1" />{org.total_students || org.students} students</Badge>}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-semibold text-gray-700 mb-2">About</h3>
                  <p className="text-gray-600">{org.about || org.description || 'No description available'}</p>
                </div>

                {(org.services || org.courses) && (org.services || org.courses).length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-700 mb-2">{org.services ? 'Services' : 'Courses'}</h3>
                    <div className="flex flex-wrap gap-2">
                      {(org.services || org.courses).map((item, idx) => (
                        <Badge key={idx} variant="secondary">{item}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {org.latitude && org.longitude && (
              <Card>
                <CardHeader><CardTitle className="flex items-center"><MapPin className="h-5 w-5 mr-2" />Location</CardTitle></CardHeader>
                <CardContent>
                  <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                    <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13}>
                      <Marker position={mapCenter} />
                    </GoogleMap>
                  </LoadScript>
                  <p className="mt-4 text-gray-700">{org.address || org.location}</p>
                  <p className="text-gray-600">{[org.city, org.state, org.pincode].filter(Boolean).join(', ')}</p>
                </CardContent>
              </Card>
            )}

            {/* Show location card for seeded orgs with location field */}
            {!org.latitude && org.location && (
              <Card>
                <CardHeader><CardTitle className="flex items-center"><MapPin className="h-5 w-5 mr-2" />Location</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-gray-700">{org.location}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div>
            <Card>
              <CardHeader><CardTitle>Contact Organization</CardTitle></CardHeader>
              <CardContent>
                {contactUnlocked ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-green-600">
                      <Unlock className="h-5 w-5" /><span className="font-semibold">Contact Unlocked</span>
                    </div>
                    {contactDetails?.organization && <p className="font-medium text-lg">{contactDetails.organization}</p>}
                    {contactDetails?.email && (
                      <div className="flex items-start space-x-2">
                        <Mail className="h-4 w-4 mt-1 text-gray-600" />
                        <div><p className="text-xs text-gray-500">Email</p><p className="font-medium">{contactDetails.email}</p></div>
                      </div>
                    )}
                    {contactDetails?.phone && (
                      <div className="flex items-start space-x-2">
                        <Phone className="h-4 w-4 mt-1 text-gray-600" />
                        <div><p className="text-xs text-gray-500">Phone</p><p className="font-medium">{contactDetails.phone}</p></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {!isApproved ? (
                      <Alert><AlertDescription>Contact available once admin approves.</AlertDescription></Alert>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">Unlock contact to connect with this organization.</p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between mb-2"><span className="text-sm">Your Balance</span><span className="font-bold">{wallet.balance} coins</span></div>
                          <div className="flex justify-between"><span className="text-sm">Unlock Price</span><span className="font-bold text-blue-600">{org.unlock_price || 0} coins</span></div>
                        </div>
                        <Button onClick={handleUnlock} disabled={unlocking || wallet.balance < (org.unlock_price || 0)} className="w-full">
                          {unlocking ? 'Unlocking...' : `Unlock Contact (${org.unlock_price || 0} coins)`}
                        </Button>
                        {wallet.balance < (org.unlock_price || 0) && <p className="text-xs text-red-600 text-center">Insufficient balance.</p>}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};